import {
  useId,
  useMemo,
  type Dispatch,
  type FormEvent,
  type ReactElement,
  type SetStateAction,
} from "react";
import { X } from "lucide-react";
import { matchesPersonQuery } from "@playbookai/types";
import {
  Button,
  Combobox,
  DatePicker,
  FilterMenu,
  FormField,
  Select,
  Switch,
  useFilterMenuController,
  type FilterSchema,
  type FilterValues,
} from "@playbookai/ui";
import type { DateTimeFormatter } from "../../../settings/format-date-time.js";
import type { AuditFacets } from "../../../api/admin-audit-api.js";
import { actionLabel, serviceLabel } from "../audit-format.js";
import {
  actorOptionsFrom,
  daysAgoInZone,
  facetHint,
  optionsFrom,
  SERVICE_FILTER_DEFAULT_DAYS,
  todayInZone,
  type FilterDraft,
} from "./audit-query.js";
import { FilterField } from "./audit-filters.js";

/** The filter bar: source service, action, actor and a calendar-day range,
    plus Apply and Clear, and the routine-activity switch. Presentational — it
    owns no state of its own. The draft, the facets and the range message all
    live on the page, because applying a filter starts a new page set on a
    query the page owns. */
export function AuditFilterBar({
  draft,
  setDraft,
  facets,
  formatter,
  canClear,
  rangeMessage,
  onSubmit,
  onClear,
  onIncludeRoutineChange,
}: {
  draft: FilterDraft;
  setDraft: Dispatch<SetStateAction<FilterDraft>>;
  facets: { data: AuditFacets | undefined; isError: boolean };
  formatter: DateTimeFormatter;
  canClear: boolean;
  rangeMessage: string | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  onIncludeRoutineChange: (includeRoutine: boolean) => void;
}): ReactElement {
  const routineHintId = useId();
  const actors = facets.data?.actors;
  const actorName = (id: string): string | undefined =>
    actors?.find((actor) => actor.id === id)?.name;
  /* The Actor filter is a FilterMenu so each actor reads as a person row
     (assign-person consistency). Its list is the recorded actors, searched in
     memory by the same any-word rule as every person search; its applied
     value IS the draft's actor, so Choose fills the draft and Apply below
     still runs the query. */
  const actorSchema = useMemo<FilterSchema>(() => {
    const options = actorOptionsFrom(actors);
    return [
      {
        id: "actor",
        label: "Actor",
        type: "single",
        options: {
          kind: "remote",
          searchable: true,
          searchPlaceholder: "Search actors",
          emptyMessage: "No actor matches.",
          load: async ({ query }) => ({
            options: options.filter((option) =>
              matchesPersonQuery({ name: option.label }, query),
            ),
            nextCursor: null,
          }),
        },
        apply: { requestParam: "actorId" },
      },
    ];
  }, [actors]);
  const actorApplied = useMemo<FilterValues>(
    () => (draft.actorId ? { actor: [draft.actorId] } : {}),
    [draft.actorId],
  );
  const actorMenu = useFilterMenuController({
    schema: actorSchema,
    applied: actorApplied,
    onAppliedChange: (next) =>
      setDraft((current) => ({
        ...current,
        actorId: next["actor"]?.[0] ?? "",
      })),
  });
  /* Choosing a service or an action fills From, because the API refuses
     either unbounded (PB-5142). Only when it is EMPTY and only when a value is
     being CHOSEN: a date the reader already picked is theirs, and clearing back
     to "Any" leaves the window alone rather than silently widening the search
     they are looking at. The 90 days are counted back from To when the reader
     has set one, so the window the page invents can never start after the end
     they chose. */
  const boundedFrom = (value: string, current: FilterDraft): string =>
    value !== "" && current.from === ""
      ? daysAgoInZone(
          SERVICE_FILTER_DEFAULT_DAYS,
          formatter.timeZone,
          current.to,
        )
      : current.from;
  return (
    <form
      onSubmit={onSubmit}
      className="mb-4 rounded-lg border border-pb-border bg-pb-shell/35 p-4"
    >
      <div className="grid gap-4">
        {/* Five filters in two rows of three: source service, action and
                    actor, then the date range beside the buttons. Action and
                    actor were cut once, as "the shape of an investigation" —
                    PB-5410 brought them back, because a record dominated by
                    machine traffic leaves an admin no other way to find the one
                    membership or role change they came for. Entity type stays
                    out: the API still accepts it, and nothing asked for it.

                    Each field carries a visible label rather than leaning on
                    the placeholder, so the name survives once a value is
                    typed. */}
        {/* The actions take the last cell of the second row, not a row of
                  their own. Below `lg` the grid falls back to two columns and
                  then one, so the buttons wrap with the fields rather than at a
                  width picked by hand. */}
        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 @min-[42.5rem]/settings-page:grid-cols-3">
          {/* A chooser, not free text. The stored values are machine
                      identifiers (`notification`), so a text box asks an admin
                      to type a string they would have to know in advance and
                      silently returns nothing when they type the readable label
                      instead. Options come from what this tenant has actually
                      recorded; labels are humanised, values are not. */}
          {/* The design system's `Select`, not `NativeSelect`. The
                      native control styles only its CLOSED state — the open
                      popup is drawn by the OS, so on this dark surface it
                      opened as an opaque white list, positioned by the OS
                      rather than anchored to the field: above and left of the
                      trigger, over the top bar and the left nav, and clipped
                      by the top of the viewport so "Any" was cut in half
                      (PB-5243). `Select` portals through the layer primitives,
                      so it themes, anchors, width-matches and stays inside the
                      viewport. `NativeSelect` stays the right choice where its
                      docblock says — dense widget-editor cells and touch
                      forms; an admin filter is a product surface. */}
          <Select
            label="Source service"
            hint={facetHint(
              facets.data?.services.length,
              "services",
              facets.isError,
              facets.data?.truncated.services,
            )}
            options={optionsFrom(facets.data?.services, serviceLabel)}
            value={draft.service}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                service: value,
                from: boundedFrom(value, current),
              }))
            }
          />
          {/* Comboboxes rather than `Select`: a tenant records tens of
                      actions and up to 500 people, so the reader types to
                      narrow instead of scrolling. The options are what this
                      tenant has recorded, and "Any" is how a choice is undone —
                      the combobox only commits an option from its list. */}
          <Combobox
            label="Action"
            hint={facetHint(
              facets.data?.actions.length,
              "actions",
              facets.isError,
              facets.data?.truncated.actions,
            )}
            options={optionsFrom(facets.data?.actions, actionLabel)}
            value={draft.action}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                action: value,
                from: boundedFrom(value, current),
              }))
            }
          />
          {/* A fieldset names the group "Actor": the menu's trigger is
                      named by the choice it shows ("Any", or the person). */}
          <FormField
            as="fieldset"
            label="Actor"
            hint={facetHint(
              facets.data?.actors.length,
              facets.data?.actors.length === 1 ? "person" : "people",
              facets.isError,
              facets.data?.truncated.actors,
            )}
            className="content-start"
          >
            <FilterMenu
              controller={actorMenu}
              title="Actor"
              triggerLabel={
                draft.actorId === ""
                  ? "Any"
                  : (actorName(draft.actorId) ?? "Selected actor")
              }
              triggerSize="md"
              showActiveCount={false}
              applyLabel="Choose"
              clearLabel="Any"
              valueLabel={(_field, value) => actorName(value)}
            />
          </FormField>
          {/* The design system's calendar, not `<input type="date">`.
                      The native control renders as whatever the browser ships —
                      a different shape and font in every one, and nothing like
                      the rest of this console.

                      Bounded in the control, not only checked on submit: the
                      audit record cannot contain a future event, and `to` can
                      never precede `from`. An out-of-range day is disabled in the
                      calendar rather than accepted and then answered with an
                      empty result, which on this surface reads as "those events
                      did not happen". */}
          <FilterField
            label="From"
            hint="Includes the whole day. No later than To."
          >
            <DatePicker
              value={draft.from}
              onChange={(day) =>
                setDraft((current) => ({ ...current, from: day }))
              }
              max={draft.to || todayInZone(formatter.timeZone)}
              placeholder="Any earlier date"
            />
          </FilterField>
          <FilterField
            label="To"
            hint="Includes the whole day. No earlier than From."
          >
            <DatePicker
              value={draft.to}
              onChange={(day) =>
                setDraft((current) => ({ ...current, to: day }))
              }
              {...(draft.from ? { min: draft.from } : {})}
              max={todayInZone(formatter.timeZone)}
              placeholder="Up to today"
            />
          </FilterField>
          <div className="content-start">
            {/* An empty label, so the buttons line up with the CONTROLS
                      rather than with the labels above them. It mirrors
                      FormField's own label box exactly, so the two stay aligned
                      if that type scale ever changes. Only from `lg`, where the
                      buttons actually sit beside a field. */}
            <span
              aria-hidden="true"
              className="mb-1.5 hidden text-xs font-medium @min-[42.5rem]/settings-page:block"
            >
              &nbsp;
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {/* "Apply", not "Search": this button does not run a text
                      search, it applies the filters above to both the table and
                      the export. Naming it Search invited the reading that the
                      range had to be searched before it would reach a file. */}
              {/* No icon. A funnel says "filter", and this button does not
                    filter — it commits the filters already chosen above. The
                    console's other commit buttons (Save changes, Add domain)
                    carry no icon either; Add and Create carry a plus. */}
              <Button type="submit" variant="primary">
                Apply
              </Button>
              {canClear ? (
                <Button type="button" variant="ghost" onClick={onClear}>
                  <X className="size-4" aria-hidden="true" />
                  Clear filters
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {/* Applies at once, unlike the fields above (PB-5410 P-7). The
                  draft-then-Apply split exists so typing does not fire a query
                  per keystroke; a switch is one deliberate change, and a
                  `role="switch"` is read as taking effect immediately. The hint
                  names what is hidden — including failed integration runs — and
                  that hidden is not deleted. */}
        <div className="grid gap-1">
          <Switch
            label="Include routine activity"
            checked={draft.includeRoutine}
            onChange={onIncludeRoutineChange}
            aria-describedby={routineHintId}
          />
          <p id={routineHintId} className="text-dense-1 text-pb-text-muted">
            Connection tickets and integration run results, including failed
            runs, are hidden by default. Hidden, not deleted: the record keeps
            every one.
          </p>
        </div>
        {rangeMessage ? (
          <p role="alert" className="text-dense-1 text-pb-red-text">
            {rangeMessage}
          </p>
        ) : null}
      </div>
    </form>
  );
}
